import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Col, FormGroup, Input, Label, Row } from "reactstrap";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { Fade } from "react-reveal";
import { Link, useNavigate, useParams } from "react-router-dom";
import { catchAsync, checkResponse } from "utilities/utilities";
import {
  ADD_CATEGORY,
  CATEGORY_DETAILS,
  UPDATE_CATEGORY,
  UPLOAD_FILE,
} from "services/ApiCalls";
import Loading from "Component/common/Loading";

/* -------------------- VALIDATION -------------------- */

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.string(), // "0" | "3"
  media: z.string().optional(),
});

/* -------------------- COMPONENT -------------------- */

const AddCategory = () => {
  const [submitLoader, setSubmitLoader] = useState(false);
  const [preview, setPreview] = useState(null);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);

  const navigate = useNavigate();
  const { id } = useParams();
  const EditId = Boolean(id);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      type: "0", // default PITCH
      media: "",
    },
  });

  const mediaUrl = watch("media");

  /* -------------------- PREVIEW UPDATE -------------------- */

  useEffect(() => {
    if (mediaUrl) setPreview(mediaUrl);
  }, [mediaUrl]);

  /* -------------------- IMAGE UPLOAD -------------------- */

  const onMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageUploadLoading(true);
    const formData = new FormData();
    formData.append("files", file);

    try {
      const res = await UPLOAD_FILE(formData);
      if (res?.status === 200) {
        const uploaded = res?.data?.data?.files?.[0];
        if (uploaded) {
          setValue("media", uploaded);
          setPreview(uploaded);
        }
      }
    } finally {
      setImageUploadLoading(false);
    }
  };

  /* -------------------- SUBMIT -------------------- */

  const submitHandler = async (formValues) => {
    setSubmitLoader(true);

    const payload = {
      _id: id,
      name: formValues.name,
      icon: formValues.media,
      type: Number(formValues.type), // ✅ FIXED
    };

    const apiToCall = EditId ? UPDATE_CATEGORY : ADD_CATEGORY;
    const res = await apiToCall(payload);

    checkResponse({
      res,
      showSuccess: true,
      navigateUrl: "/category",
      navigate,
      setLoader: setSubmitLoader,
    });
  };

  /* -------------------- GET DETAILS (EDIT) -------------------- */

  const getData = catchAsync(async () => {
    const res = await CATEGORY_DETAILS(id);

    checkResponse({
      res,
      setData: (data) => {
        reset({
          name: data?.name || "",
          type: String(data?.type ?? "0"), // ✅ FIXED
          media: data?.icon || "",
        });

        if (data?.icon) setPreview(data.icon);
      },
    });
  });

  useEffect(() => {
    if (EditId) getData();
  }, [EditId]);

  /* -------------------- UI -------------------- */

  return (
    <Fade>
      <section className="addresources comon_dashpad">
        <form onSubmit={handleSubmit(submitHandler)}>
          <div className="comom_fillter_set text-start">
            <div className="resource_headwrap d-flex align-items-center justify-content-between">
              <h5 className="fillter_head">
                {EditId ? "Edit Category" : "Add Category"}
              </h5>
            </div>

            {/* Breadcrumb */}
            <div className="breadcrumb_set mt-3">
              <Breadcrumb>
                <BreadcrumbItem>
                  <Link to="/category">Manage Category</Link>
                </BreadcrumbItem>
                <BreadcrumbItem active>
                  {EditId ? "Edit Category" : "Add Category"}
                </BreadcrumbItem>
              </Breadcrumb>
            </div>

            {/* Form */}
            <div className="addresources_bottom_form rounded-3 p-3">
              <Row>
                {/* Name */}
                <Col lg={6}>
                  <FormGroup>
                    <Label style={{ color: "white" }}>Name</Label>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => <Input type="text" {...field} />}
                    />
                    {errors.name && (
                      <p className="text-danger">{errors.name.message}</p>
                    )}
                  </FormGroup>
                </Col>

                {/* Type */}
                <Col lg={6}>
                  <FormGroup>
                    <Label style={{ color: "white" }}>Type</Label>
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <select {...field} className="form-control">
                          <option value="0">PITCH</option>
                          <option value="3">EVENTS</option>
                        </select>
                      )}
                    />
                  </FormGroup>
                </Col>

                {/* Icon */}
                <Col lg={6}>
                  <FormGroup>
                    <Label style={{ color: "white" }}>Icon</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={onMediaUpload}
                    />

                    {imageUploadLoading && <p>Uploading...</p>}

                    {preview && (
                      <div
                        className="mt-2 position-relative"
                        style={{ display: "inline-block" }}
                      >
                        <img
                          src={preview}
                          alt="Preview"
                          style={{ maxWidth: 150, borderRadius: 8 }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setPreview(null);
                            setValue("media", "");
                          }}
                          className="btn btn-danger position-absolute"
                          style={{
                            top: -10,
                            right: -10,
                            borderRadius: "50%",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              {/* Submit */}
              <Col lg={6} className="mt-4">
                <Button
                  className="export_csv w-100"
                  style={{ height: 50 }}
                  disabled={submitLoader}
                >
                  {submitLoader ? <Loading fullSize /> : "Submit"}
                </Button>
              </Col>
            </div>
          </div>
        </form>
      </section>
    </Fade>
  );
};

export default AddCategory;
